/**
 * IdleWatchdog — visible warning + auto-logout for kiosk mode.
 *
 * Wraps useIdleLogout with a small countdown UI that fades in during
 * the last 30 s. Tapping anywhere resets via the hook's input
 * listeners; the watchdog component itself doesn't need any taps.
 */

import { logout } from '@cardioq/api-client';
import { useNavigate } from 'react-router-dom';
import { useFormFactor } from '@/hooks/useFormFactor';
import { useIdleLogout } from '@/hooks/useIdleLogout';
import { useAuth } from '@/hooks/useAuth';
import { PATIENT_SIGN_IN } from '@/lib/signInPath';

export function IdleWatchdog() {
  const { isKiosk, idleTimeoutMs } = useFormFactor();
  const { logout: clearLocalAuth } = useAuth();
  const navigate = useNavigate();

  const { remainingMs, isWarning } = useIdleLogout({
    timeoutMs: idleTimeoutMs,
    warnAheadMs: 30_000,
    onTimeout: async () => {
      await logout();
      clearLocalAuth();
      navigate(PATIENT_SIGN_IN, { replace: true });
    },
  });

  if (!isKiosk || !isWarning) return null;

  const seconds = Math.ceil(remainingMs / 1000);

  return (
    <div
      role="alert"
      aria-live="polite"
      className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full border border-brand-pulse-300/40 bg-brand-surface px-5 py-3 shadow-lg backdrop-blur-md animate-fade-in"
    >
      <p className="text-sm font-semibold text-brand-slate-100">
        Signing you out in{' '}
        <span className="font-mono text-brand-pulse-300">{seconds}s</span>{' '}
        for privacy — tap anywhere to stay.
      </p>
    </div>
  );
}
